/* línkè · 主题中枢 ================================================
   夜空(默认深) / 日落(暖·浅) / 玫瑰(玫红系) / 初雪(冷·浅)。状态存 localStorage，
   通过 <html data-theme="..."> 让 tokens.css 对应主题变量生效。
   dot = 色板上的主色点；meta = 浏览器顶栏 chrome 颜色（theme-color）。
   ⚠ 「往 THEMES 里补一行」只是第一步——新主题还要接 tokens.css(主题块+强调色
   浅主题名单)、bg.js(THEME_PAGE_BG/THEME_BG/RECIPES/currentTheme 白名单)、
   textColor.js(THEME_TEXT/白名单)、themeSkin.js(THEME_IDS)，以及 Sky/Chat/
   global.css 里几处按「浅底」写的覆盖。2026-07-29 加「初雪」时逐处对过。
   （原在 App.jsx 内，2026-06-27 抽出来给侧边栏复用，逻辑原样保留。）
   ================================================================ */
import { useState, useEffect, useRef } from 'react';
import { applyWallpaperTopColor } from './wallpaper.js';
import { applyBg, getBg } from './bg.js';
import { applyStoredTextColors } from './textColor.js';
import { applyStoredAccent } from './accent.js';
import { applyStoredUserBubble } from './userBubble.js';
import { ensureSeeded, handoffTheme, notifySkinChanged } from './themeSkin.js';

export const STORAGE_THEME = 'lingke.theme';
export const THEMES = [
  { id: 'dark', name: '夜空', dot: '#9a86c8', meta: '#15102a' },
  { id: 'light', name: '日落', dot: '#b0561c', meta: '#f9efe4' },
  { id: 'rose', name: '玫瑰', dot: '#ff00a5', meta: '#fbeee9' },
  { id: 'snow', name: '初雪', dot: '#2f6ba3', meta: '#f5f8fc' },
];
export const THEME_IDS = THEMES.map((t) => t.id);

// 同步 meta theme-color = 顶栏 chrome 色。
// 「换整个 meta 元素」而非只改 content：Safari 对 theme-color 的实时刷新更认换元素。
// 关键：必须清掉【所有】旧的 theme-color meta，不能只删第一个。首帧脚本也会 append 一个，
// 一旦同时存在两个，Safari 读的是文档里【最靠前】那个，旧色就会赖着不走（表现为「切一次后黏住」）。
// 浏览器里上下栏会即时变色；standalone 状态栏在下次启动由 index.html 开机脚本上对色（iOS 限制）。
// iOS Safari 在【系统深色模式】下会无视「浅色」theme-color、把上下栏锁深（表现为「浅→深能切、
// 深→浅切不回」）。对策：按 HTML 规范「取第一个 media 匹配的 theme-color」，显式给 light / dark
// 两个配色方案各下一个 meta、且都指向当前色 → 不管系统深浅，Safari 都被明确告知用我们这个色。
// 顺序：scheme 专用在前（规范取首个匹配的），无 media 兜底放最后，给不认 media 的旧 webview。
function setThemeColorMetas(color) {
  document.querySelectorAll('meta[name="theme-color"]').forEach((el) => el.remove());
  const add = (media) => {
    const m = document.createElement('meta');
    m.setAttribute('name', 'theme-color');
    if (media) m.setAttribute('media', media);
    m.setAttribute('content', color);
    document.head.appendChild(m);
  };
  add('(prefers-color-scheme: light)');
  add('(prefers-color-scheme: dark)');
  add(null);
}

export function applyTheme(id) {
  const root = document.documentElement;
  if (id === 'dark') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', id);
  // 自选背景色：按【新主题】的阶梯配方重算整套底色（须在 data-theme 之后、壁纸顶色之前，
  // 因为壁纸顶色要读可能已被背景色改过的 --void-rgb）。没选色则原样。
  applyBg(getBg());
  // 自选文字颜色：@ 预设按新主题的明度阶梯重算（自定义 #hex 保持字面）。没选则原样。
  applyStoredTextColors();
  // 顶栏色：有壁纸时取【壁纸顶部色】（跟当前主题遮罩现算合成，故须在 data-theme 之后），
  // 否则用主题色 THEMES.meta（已对齐 --sky-grad 顶）。
  const t = THEMES.find((x) => x.id === id);
  const wp = applyWallpaperTopColor();
  setThemeColorMetas(wp || (t ? t.meta : '#15102a'));
}

/* 壁纸增删 / 遮罩浓度变动后：主题不变，只重算并刷新顶栏色（状态栏 + --wp-top 色带）。 */
export function refreshChromeColor() {
  let id = 'dark';
  try {
    const v = localStorage.getItem(STORAGE_THEME);
    if (THEME_IDS.includes(v)) id = v;
  } catch {}
  const t = THEMES.find((x) => x.id === id);
  const wp = applyWallpaperTopColor();
  setThemeColorMetas(wp || (t ? t.meta : '#15102a'));
}

/* React hook：[theme, setTheme]。改 theme 时自动 applyTheme + 存 localStorage。 */
export function useTheme() {
  const [theme, setTheme] = useState(() => {
    try {
      const v = localStorage.getItem(STORAGE_THEME);
      return THEME_IDS.includes(v) ? v : 'dark';
    } catch {
      return 'dark';
    }
  });
  /* 上一个主题——用来做「离开时收好、进来时铺开」的交接。
     初次挂载时为 null（没有上一个主题，不做交接，只把她现有这套分家给另外两个主题）。 */
  const prevTheme = useRef(null);
  useEffect(() => {
    if (prevTheme.current === null) {
      ensureSeeded(); // 首次：把她调好的颜色复制给另外两个主题，切过去不会变回出厂色
    } else if (prevTheme.current !== theme) {
      // 换主题：旧主题收好自己那套 → 新主题那套搬回原 key（见 themeSkin.js 文件头）
      handoffTheme(prevTheme.current, theme);
    }
    prevTheme.current = theme;

    applyTheme(theme);
    /* applyTheme 只重算背景色/文字色/壁纸顶色；强调色和气泡色也得跟着新主题那套刷新。 */
    applyStoredAccent();
    applyStoredUserBubble();
    /* 壁纸图片在组件里用 useState 缓存着，localStorage 变了它不知道——广播一声让它重读。 */
    notifySkinChanged();

    try {
      localStorage.setItem(STORAGE_THEME, theme);
    } catch {}
  }, [theme]);
  return [theme, setTheme];
}
